import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
import { FormControl, FormGroup } from '@angular/forms'
import {
  DisplayDateFormat,
  DisplayDateTimeFormat,
  DisplayTimeFormat,
} from '@northfork/basics'
import { Subject } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'
import {
  ColumnFilterModel,
  FilterEditorRowModel,
  FilterEditorRowValueType,
} from '../../models/filter-editor.model'
import { FilterOperator } from '../../models/filter-operator.model'
import {
  FilterValueInputType,
  SetFilterOptionModel,
} from '../../models/filter.model'

@Component({
  selector: 'hs-filter-editor-row',
  templateUrl: './filter-editor-row.component.html',
  styleUrls: ['./filter-editor-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterEditorRowComponent implements OnInit, OnDestroy {
  displayDateFormat = DisplayDateFormat
  displayTimeFormat = DisplayTimeFormat
  displayDateTimeFormat = DisplayDateTimeFormat

  @Input() columnFilters: ColumnFilterModel[] = []
  @Input() rowModel: FilterEditorRowModel = { id: -1 }
  @Input() isLast = false

  @Output() rowModelChange = new EventEmitter<FilterEditorRowModel>()
  @Output() addNewFilterRow = new EventEmitter<void>()

  columnControl = new FormControl(null)
  operatorControl = new FormControl({ value: null, disabled: true })
  filterValueControl = new FormControl({ value: null, disabled: true })
  filterToValueControl = new FormControl({ value: null, disabled: true })
  valueGroup = new FormGroup({
    filterValue: new FormControl(null),
    filterToValue: new FormControl(null),
  })
  filterGroup = new FormGroup({
    column: this.columnControl,
    operator: this.operatorControl,
    valueGroup: this.valueGroup,
  })

  rowId = -1
  selectedColumnFilter?: ColumnFilterModel
  availableOperators: FilterOperator[] = []
  selectedOperator?: FilterOperator
  filterValueInputType?: FilterValueInputType
  setFilterOptions: Promise<SetFilterOptionModel[]> = Promise.resolve([])

  private readonly destroy$ = new Subject<void>()

  constructor(private readonly cd: ChangeDetectorRef) {}

  ngOnInit() {
    this.rowId = this.rowModel.id
    this.setupFormControls()
    this.setupSubscriptions()
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  setupFormControls() {
    // Get row model values
    const { column, operator, filterValue, filterToValue } = this.rowModel

    // Set the filter group
    this.filterGroup.setValue(
      {
        column: column ?? null,
        operator: operator ?? null,
        valueGroup: {
          filterValue: filterValue ?? null,
          filterToValue: filterToValue ?? null,
        },
      },
      { emitEvent: false },
    )

    this.onColumnChange(column)
    this.onOperatorChange(operator)
  }

  setupSubscriptions() {
    this.columnControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(column => {
        this.onColumnChange(column)
        this.resetOperatorControl()
      })

    this.operatorControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(operator => {
        this.onOperatorChange(operator)
        this.resetFilterValueControl()
      })

    this.filterGroup.valueChanges
      .pipe(takeUntil(this.destroy$), debounceTime(20))
      .subscribe(({ column, operator, valueGroup }) => {
        this.updateRowModel(
          column,
          operator,
          valueGroup.filterValue,
          valueGroup.filterToValue,
        )
      })
  }

  onColumnChange(selectedColumnFilter?: ColumnFilterModel) {
    this.selectedColumnFilter = selectedColumnFilter
    this.updateAvailableOperators()
    this.updateIsOperatorDisabled()

    if (this.isLast && selectedColumnFilter) {
      this.addNewFilterRow.emit()
    }
  }

  onOperatorChange(selectedOperator?: FilterOperator) {
    this.selectedOperator = selectedOperator
    this.updateFilterValueType()
    this.updateIsFilterValueDisabled()
  }

  updateIsOperatorDisabled() {
    if (this.selectedColumnFilter) {
      this.operatorControl.enable({ emitEvent: false })
      return
    }
    this.operatorControl.disable({ emitEvent: false })
  }

  updateIsFilterValueDisabled() {
    if (this.selectedOperator) {
      this.valueGroup.enable({ emitEvent: false })
      return
    }
    this.valueGroup.disable({ emitEvent: false })
  }

  resetOperatorControl() {
    const selectedColumnFilter = this.selectedColumnFilter
    if (!selectedColumnFilter) {
      this.operatorControl.setValue(null)
      return
    }

    const operators = selectedColumnFilter.operators
    this.selectedOperator = operators[0] ?? null
    this.operatorControl.setValue(this.selectedOperator)
  }

  resetFilterValueControl() {
    this.valueGroup.setValue({ filterValue: null, filterToValue: null })
  }

  updateAvailableOperators() {
    this.availableOperators = this.selectedColumnFilter?.operators ?? []
  }

  updateFilterValueType() {
    if (this.selectedOperator) {
      this.filterValueInputType =
        this.selectedColumnFilter?.filterValueInputs?.get(
          this.selectedOperator.type,
        )
    } else {
      this.filterValueInputType = undefined
    }

    this.updateSetFilterOptions()
  }

  updateSetFilterOptions() {
    const filterType = this.selectedColumnFilter?.filterType
    if (filterType && filterType.type === 'set') {
      this.setFilterOptions = filterType.options
    } else {
      this.setFilterOptions = Promise.resolve([])
    }
  }

  updateRowModel(
    column?: ColumnFilterModel,
    operator?: FilterOperator,
    filterValue?: FilterEditorRowValueType,
    filterToValue?: FilterEditorRowValueType,
  ) {
    this.rowModelChange.emit({
      id: this.rowId,
      column: column ?? undefined,
      operator: operator ?? undefined,
      filterValue: filterValue ?? undefined,
      filterToValue: filterToValue ?? undefined,
    })
  }
}
